// Theme-aware design tokens for the customer embeds — inline styles only.
// Structural tokens are theme-invariant; only `colors` swaps per theme.
// The embed renders inside a host iframe/page we do not style; every value
// here is applied via React inline `style` props off a TokenSet.

const STRUCTURAL = {
  fontFamily: 'Inter, "Inter Placeholder", system-ui, sans-serif',
  borderRadiusSm: 4,
  borderRadius: 8,
  borderRadiusLg: 12,
  inputPadding: 12,
  inputFontSize: 14,
  buttonHeight: 44,
  spacing: { xs: 4, sm: 8, md: 16, lg: 24, xl: 32 },
  fontSize: { xs: 12, sm: 14, md: 16, lg: 20, xl: 24, xxl: 32 },
  lineHeight: { tight: 1.25, normal: 1.5, relaxed: 1.75 },
} as const;

export interface ThemeColors {
  primary: string; primaryHover: string; primaryTint: string;
  text: string; textSecondary: string; placeholder: string;
  error: string; errorBg: string;
  successGreen: string; successBg: string;
  white: string; inputBg: string;
  border: string; borderFocus: string;
  disabled: string; disabledBg: string;
  // surfaces
  surface: string; surfaceSunken: string;
  cardBg: string; cardBorder: string; cardShadow: string;
  // marketplace
  chargeOneTime: string; chargeRecurring: string; chargeRecurringTint: string; priceOriginal: string;
  // stepper
  stepperActive: string; stepperComplete: string; stepperPending: string;
}

export interface TokenSet {
  fontFamily: string; borderRadiusSm: number; borderRadius: number; borderRadiusLg: number;
  inputPadding: number; inputFontSize: number; buttonHeight: number;
  spacing: typeof STRUCTURAL.spacing;
  fontSize: typeof STRUCTURAL.fontSize;
  lineHeight: typeof STRUCTURAL.lineHeight;
  colors: ThemeColors;
}

export const LIGHT_COLORS: ThemeColors = {
  primary: "#1D59FF", primaryHover: "#1545CC", primaryTint: "#EFF3FF",
  text: "#111827", textSecondary: "#6B7280", placeholder: "#9CA3AF",
  error: "#DC2626", errorBg: "#FEF2F2",
  successGreen: "#16A34A", successBg: "#F0FDF4",
  white: "#FFFFFF", inputBg: "rgba(187,187,187,0.10)",
  border: "#D1D5DB", borderFocus: "#1D59FF",
  disabled: "#9CA3AF", disabledBg: "#F3F4F6",
  surface: "#FFFFFF", surfaceSunken: "#F9FAFB",
  cardBg: "#FFFFFF", cardBorder: "#E5E7EB",
  cardShadow: "0 1px 2px rgba(16,24,40,0.06), 0 1px 3px rgba(16,24,40,0.10)",
  chargeOneTime: "#1D59FF", chargeRecurring: "#7C3AED", chargeRecurringTint: "rgba(124,58,237,0.14)", priceOriginal: "#9CA3AF",
  stepperActive: "#1D59FF", stepperComplete: "#16A34A", stepperPending: "#D1D5DB",
};

export const DARK_COLORS: ThemeColors = {
  primary: "#5B8DEF", primaryHover: "#7AA3F5", primaryTint: "rgba(91,141,239,0.16)",
  text: "#F4F4F5", textSecondary: "#A1A1AA", placeholder: "#71717A",
  error: "#F87171", errorBg: "rgba(248,113,113,0.12)",
  successGreen: "#4ADE80", successBg: "rgba(74,222,128,0.12)",
  white: "#FFFFFF", inputBg: "rgba(255,255,255,0.05)",
  border: "#3F3F46", borderFocus: "#5B8DEF",
  disabled: "#52525B", disabledBg: "#27272A",
  surface: "#18181B", surfaceSunken: "#131316",
  cardBg: "#1F1F23", cardBorder: "#3F3F46",
  cardShadow: "0 1px 2px rgba(0,0,0,0.4), 0 1px 3px rgba(0,0,0,0.5)",
  chargeOneTime: "#5B8DEF", chargeRecurring: "#A78BFA", chargeRecurringTint: "rgba(167,139,250,0.16)", priceOriginal: "#71717A",
  stepperActive: "#5B8DEF", stepperComplete: "#4ADE80", stepperPending: "#3F3F46",
};

export const LIGHT_TOKENS: TokenSet = { ...STRUCTURAL, colors: LIGHT_COLORS };
export const DARK_TOKENS: TokenSet = { ...STRUCTURAL, colors: DARK_COLORS };
